import React, { useState } from 'react';
import { Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { errMsg } from '@/services/staffAttendanceApi';
import { tallyApi } from '@/services/ewayBillApi';
import { fmtDT } from '@/components/ewayBill/shared';
import { CreatePlan, TallyNames } from '@/components/ewayBill/TallyNames';

const NEXT = {
  QUEUED: 'Waiting for the Tally link to create the voucher in Tally.',
  IN_TALLY: 'Voucher is in Tally. In TallyPrime: Alt+G → e-Way Bill → Exchange → Send for e-Way Bill.',
  TALLY_ERROR: 'Tally refused it — fix the reason below (or pick another Tally name below), then Retry. Missing customer ledgers and Rates Master stock items are created by WMS again on Retry.',
  ACTIVE: 'To cancel or change Part B, do it in Tally (e-Way Bill report) — WMS picks it up automatically.',
};

const MastersState = ({ t }) => {
  if (!t.create) return null;
  const r = t.masters_result;
  return t.masters_at ? (
    <p className="text-emerald-800" data-testid="tally-bill-masters">
      Created in Tally {fmtDT(t.masters_at)}{r ? ` · ${r.created} new${r.existing ? `, ${r.existing} already there` : ''}` : ''}
      {t.party?.create ? <> · ledger <b>{t.party.tally}</b></> : ''}
    </p>
  ) : <CreatePlan c={t.create} testId="tally-bill-masters" />;
};

/** Tally route: voucher, ledger, Tally replies, Retry / Discard. */
export const TallyBillPanel = ({ b, onChanged, onMapped }) => {
  const [busy, setBusy] = useState('');
  const t = b.tally || {};
  const act = (kind) => {
    setBusy(kind);
    tallyApi[kind](b.id).then((x) => { toast.success(kind === 'retry' ? 'Sent to Tally again' : 'Request discarded'); onChanged(x); })
      .catch((e) => toast.error(errMsg(e, 'Failed'), { duration: 8000 })).finally(() => setBusy(''));
  };
  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-3 text-sm space-y-2" data-testid="tally-bill-panel">
      <div className="flex flex-wrap gap-x-6 gap-y-1">
        <span>Tally voucher <b>{t.vch_type} {b.doc_no}</b></span>
        <span>Party ledger <b data-testid="tally-bill-ledger">{t.party?.tally}</b></span>
        {t.pushed_at && <span>In Tally since {fmtDT(t.pushed_at)}</span>}
      </div>
      {NEXT[b.status] && <p className="text-gray-700" data-testid="tally-bill-next">{NEXT[b.status]}</p>}
      {b.status !== 'CANCELLED' && <MastersState t={t} />}
      {t.error && <p className="rounded bg-rose-50 border border-rose-200 px-2 py-1 text-rose-800" data-testid="tally-bill-error">Tally: {t.error}</p>}
      {(t.ewb_errors || []).length > 0 && (
        <ul className="list-disc pl-5 text-rose-700" data-testid="tally-bill-ewb-errors">{t.ewb_errors.map((x) => <li key={x}>{x}</li>)}</ul>
      )}
      {b.tally_names && <TallyNames tally={b.tally_names} onMapped={onMapped} allowChange />}
      {(b.can_retry || b.can_discard) && (
        <div className="flex justify-end gap-2">
          {b.can_discard && <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => act('discard')} disabled={!!busy} data-testid="tally-discard-btn">
            {busy === 'discard' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}Discard</Button>}
          {b.can_retry && <Button size="sm" onClick={() => act('retry')} disabled={!!busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="tally-retry-btn">
            {busy === 'retry' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}Retry</Button>}
        </div>
      )}
    </div>
  );
};

export default TallyBillPanel;
